import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Check, Minus, Plus } from 'lucide-react';
import { COURSES, type MenuItem, type ModifierGroup } from '@sebastian/shared';
import { Button, Field, Input, Modal, Textarea } from './ui';
import { useCurrency } from '../lib/currency';

export interface ItemDraft {
  quantity: number;
  course: number;
  notes: string | null;
  modifiers: { modifierOptionId: string; quantity: number }[];
}

type Selection = Record<string, Record<string, number>>;

function isSatisfied(group: ModifierGroup, chosen: Record<string, number> | undefined): boolean {
  const count = Object.keys(chosen ?? {}).length;
  const min = group.isRequired ? Math.max(1, group.minSelections) : group.minSelections;
  if (count < min && (group.isRequired || count > 0)) return false;
  if (group.maxSelections !== null && count > group.maxSelections) return false;
  return true;
}

function describe(group: ModifierGroup): string {
  const min = group.isRequired ? Math.max(1, group.minSelections) : group.minSelections;
  if (group.maxSelections === 1 && min === 1) return 'Required · choose 1';
  const parts: string[] = [group.isRequired ? 'Required' : 'Optional'];
  if (min > 0) parts.push(`min ${min}`);
  if (group.maxSelections !== null) parts.push(`max ${group.maxSelections}`);
  return parts.join(' · ');
}

/**
 * Item configuration sheet: quantity, modifiers, course and note in one pass,
 * so adding a dish is a single interaction on the floor. Every tapped item
 * comes through here, plain ones included, so the quantity is always asked for
 * before anything reaches the order.
 *
 * `busy` is on while the add is in flight: the sheet stays open, keeps the
 * draft, and refuses to close, because the server has not confirmed yet.
 */
export function ModifierDialog({
  item,
  open,
  defaultCourse,
  busy = false,
  onCancel,
  onConfirm,
}: {
  item: MenuItem | null;
  open: boolean;
  defaultCourse: number;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (draft: ItemDraft) => void;
}) {
  const { money } = useCurrency();
  const [selection, setSelection] = useState<Selection>({});
  const [quantity, setQuantity] = useState(1);
  // The box is typed into as well as stepped, so it keeps its own string: a
  // half-typed or momentarily empty field must not collapse back to 1.
  const [quantityText, setQuantityText] = useState('1');
  const [course, setCourse] = useState(defaultCourse);
  const [notes, setNotes] = useState('');
  const quantityRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open || !item) return;
    setQuantity(1);
    setQuantityText('1');
    setCourse(defaultCourse);
    setNotes('');
    // Pre-select the default option of each single-choice group.
    const initial: Selection = {};
    for (const group of item.modifierGroups) {
      const preset = group.options.find((option) => option.isDefault && option.isActive);
      if (preset && group.maxSelections === 1) initial[group.id] = { [preset.id]: 1 };
    }
    setSelection(initial);
  }, [open, item, defaultCourse]);

  // A plain item has nothing to choose, so the sheet is only about the number:
  // put the cursor in it, selected, so a count can be typed straight away.
  useEffect(() => {
    if (!open || !item || item.modifierGroups.length > 0) return;
    const id = window.setTimeout(() => quantityRef.current?.select(), 0);
    return () => window.clearTimeout(id);
  }, [open, item]);

  const groups = item?.modifierGroups ?? [];

  const unmetGroup = useMemo(
    () => groups.find((group) => !isSatisfied(group, selection[group.id])),
    [groups, selection],
  );

  const unitPriceCents = useMemo(() => {
    if (!item) return 0;
    let total = item.priceCents;
    for (const group of groups) {
      for (const [optionId, count] of Object.entries(selection[group.id] ?? {})) {
        const option = group.options.find((candidate) => candidate.id === optionId);
        if (option) total += option.priceDeltaCents * count;
      }
    }
    return total;
  }, [item, groups, selection]);

  if (!item) return null;

  // An untracked item has no ceiling but the sanity cap; a tracked one cannot
  // be ordered past what is left, so the box refuses the number rather than
  // letting the server reject the whole add a round trip later.
  const stockLimit = item.trackStock ? Math.max(0, item.stockQuantity ?? 0) : null;
  const maxQuantity = stockLimit === null ? 99 : Math.min(99, stockLimit);
  const soldOut = stockLimit === 0;

  const toggle = (group: ModifierGroup, optionId: string) => {
    setSelection((current) => {
      const chosen = { ...(current[group.id] ?? {}) };
      if (chosen[optionId]) {
        delete chosen[optionId];
      } else {
        // A single-choice group replaces rather than accumulates.
        if (group.maxSelections === 1) {
          return { ...current, [group.id]: { [optionId]: 1 } };
        }
        if (group.maxSelections !== null && Object.keys(chosen).length >= group.maxSelections) {
          return current;
        }
        chosen[optionId] = 1;
      }
      return { ...current, [group.id]: chosen };
    });
  };

  const commitQuantity = (next: number) => {
    const clamped = Math.min(maxQuantity, Math.max(1, Math.round(next) || 1));
    setQuantity(clamped);
    setQuantityText(String(clamped));
  };

  const setOptionQuantity = (groupId: string, optionId: string, next: number) => {
    setSelection((current) => {
      const chosen = { ...(current[groupId] ?? {}) };
      if (next <= 0) delete chosen[optionId];
      else chosen[optionId] = Math.min(20, next);
      return { ...current, [groupId]: chosen };
    });
  };

  const confirm = () => {
    if (busy || unmetGroup || soldOut) return;
    const modifiers = Object.entries(selection).flatMap(([, options]) =>
      Object.entries(options).map(([modifierOptionId, count]) => ({
        modifierOptionId,
        quantity: count,
      })),
    );
    onConfirm({ quantity, course, notes: notes.trim() || null, modifiers });
  };

  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onCancel}
      title={item.name}
      subtitle={item.description ?? undefined}
      size="lg"
      footer={
        <div className="flex w-full items-center justify-between gap-4">
          <div className="tnum text-sm text-ink-faint">
            {quantity} × {money(unitPriceCents)} ={' '}
            <span className="font-display text-xl text-accent-strong">
              {money(unitPriceCents * quantity)}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" disabled={busy} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="lg"
              loading={busy}
              disabled={Boolean(unmetGroup) || busy || soldOut}
              onClick={confirm}
            >
              {busy
                ? 'Adding…'
                : soldOut
                  ? 'Out of stock'
                  : unmetGroup
                    ? `Choose ${unmetGroup.name}`
                    : `Confirm · ${quantity}`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-7">
        {groups.map((group) => (
          <section key={group.id}>
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <h3 className="font-display text-lg text-ink">{group.name}</h3>
              <span
                className={clsx(
                  'text-[11px] font-semibold uppercase tracking-wider',
                  group.isRequired && !isSatisfied(group, selection[group.id])
                    ? 'text-caution'
                    : 'text-ink-faint',
                )}
              >
                {describe(group)}
              </span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {group.options
                .filter((option) => option.isActive)
                .map((option) => {
                  const count = selection[group.id]?.[option.id] ?? 0;
                  const active = count > 0;
                  return (
                    <div
                      key={option.id}
                      className={clsx(
                        'flex items-center gap-3 rounded-lg border px-3 py-2.5 transition',
                        active
                          ? 'border-accent bg-accent/10'
                          : 'border-line bg-canvas hover:border-line-strong',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => toggle(group, option.id)}
                        className="flex flex-1 items-center gap-3 text-left"
                      >
                        <span
                          className={clsx(
                            'flex h-5 w-5 shrink-0 items-center justify-center border transition',
                            group.maxSelections === 1 ? 'rounded-full' : 'rounded',
                            active ? 'border-accent bg-accent text-white' : 'border-line-strong',
                          )}
                        >
                          {active ? <Check size={13} strokeWidth={3} /> : null}
                        </span>
                        <span className="flex-1 text-sm text-ink">{option.name}</span>
                        {option.priceDeltaCents !== 0 ? (
                          <span className="tnum text-sm text-accent">
                            {option.priceDeltaCents > 0 ? '+' : '−'}
                            {money(Math.abs(option.priceDeltaCents))}
                          </span>
                        ) : null}
                      </button>

                      {group.allowQuantity && active ? (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            aria-label={`Fewer ${option.name}`}
                            className="flex h-7 w-7 items-center justify-center rounded border border-line text-ink-soft hover:text-ink"
                            onClick={() => setOptionQuantity(group.id, option.id, count - 1)}
                          >
                            <Minus size={13} />
                          </button>
                          <span className="tnum w-5 text-center text-sm text-ink">{count}</span>
                          <button
                            type="button"
                            aria-label={`More ${option.name}`}
                            className="flex h-7 w-7 items-center justify-center rounded border border-line text-ink-soft hover:text-ink"
                            onClick={() => setOptionQuantity(group.id, option.id, count + 1)}
                          >
                            <Plus size={13} />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
            </div>
          </section>
        ))}

        <section className="grid gap-5 sm:grid-cols-2">
          <Field
            label="Quantity"
            hint={
              stockLimit === null
                ? 'Type a number, or step it.'
                : soldOut
                  ? 'None left in stock.'
                  : quantity >= maxQuantity
                    ? `That is all ${stockLimit} left.`
                    : `${stockLimit} left in stock.`
            }
          >
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                className="h-12 w-12 p-0"
                aria-label="Fewer"
                disabled={busy || soldOut || quantity <= 1}
                onClick={() => commitQuantity(quantity - 1)}
              >
                <Minus size={18} />
              </Button>
              <Input
                ref={quantityRef}
                type="number"
                inputMode="numeric"
                min={1}
                max={maxQuantity}
                step={1}
                aria-label="Quantity to add"
                disabled={busy || soldOut}
                value={quantityText}
                onChange={(event) => {
                  const raw = event.target.value;
                  setQuantityText(raw);
                  const parsed = Number.parseInt(raw, 10);
                  if (!Number.isFinite(parsed)) return;
                  const clamped = Math.min(maxQuantity, Math.max(1, parsed));
                  setQuantity(clamped);
                  // Typing past what is in stock snaps the box back at once,
                  // rather than letting a number stand that cannot be ordered.
                  if (parsed > maxQuantity) setQuantityText(String(clamped));
                }}
                onFocus={(event) => event.target.select()}
                onBlur={() => commitQuantity(quantity)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitQuantity(quantity);
                    confirm();
                  }
                }}
                className="tnum w-20 text-center font-display text-2xl"
              />
              <Button
                variant="secondary"
                className="h-12 w-12 p-0"
                aria-label="More"
                disabled={busy || soldOut || quantity >= maxQuantity}
                onClick={() => commitQuantity(quantity + 1)}
              >
                <Plus size={18} />
              </Button>
            </div>
          </Field>

          <Field label="Course">
            <div className="flex flex-wrap gap-1.5">
              {COURSES.map((number) => (
                <button
                  key={number}
                  type="button"
                  onClick={() => setCourse(number)}
                  className={clsx(
                    'h-11 w-11 rounded-lg border text-sm font-semibold transition',
                    course === number
                      ? 'border-accent bg-accent/15 text-accent-strong'
                      : 'border-line text-ink-soft hover:border-line-strong hover:text-ink',
                  )}
                >
                  {number}
                </button>
              ))}
            </div>
          </Field>
        </section>

        <Field label="Kitchen note" hint="Allergies, cooking preference, anything the pass must see.">
          <Textarea
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="No onion · Allergy: peanuts · Extra sauce"
          />
        </Field>
      </div>
    </Modal>
  );
}
